import { api } from './api';
import type { StudioRecipe } from '@sr-test-wizard/shared';
import type { EnvLinkRow } from './envLinksApi';

export type RecipeSource = 'user' | 'bundled';

export interface RecipeListEntry {
  slug: string;
  studio: string;
  source: RecipeSource;
  hasBundled: boolean;
  hasUser: boolean;
  mtimeMs: number;
  status: 'ok' | 'invalid' | 'missing';
  error: string | null;
}

export interface RecipeFetchResponse {
  slug: string;
  source: RecipeSource;
  recipe: StudioRecipe | null;
  status: 'ok' | 'invalid';
  error: string | null;
}

export interface RecipeSaveResponse {
  slug: string;
  recipe: StudioRecipe;
  path: string;
  source: 'user';
}

export interface RecipeDeleteResponse {
  slug: string;
  removed: boolean;
  fellBackToBundled: boolean;
}

export interface DraftSuccessResponse {
  recipe: StudioRecipe;
  warnings: string[];
}

export interface DraftFailureResponse {
  error: string;
  raw: unknown;
  schemaIssues: string[];
}

export interface ConfluenceSearchHit {
  id: string;
  title: string;
  url: string;
  spaceKey: string | null;
  excerpt: string | null;
}

export interface GitLabProjectSearchHit {
  id: number;
  name: string;
  path: string;
  pathWithNamespace: string;
  webUrl: string;
  defaultBranch: string | null;
  matchScore: number;
}

export interface ConfluenceSyncResponse {
  slug: string;
  recipe: StudioRecipe;
  path: string;
  warnings: string[];
  page: {
    id: string;
    title: string;
    version: number;
    url: string;
  };
}

export interface SourceStatusResponse {
  kind: 'pdf' | 'confluence' | null;
  stale: boolean;
  upstreamVersion: number | null;
  localVersion: number | null;
  lastSyncedAt: string | null;
  pageTitle?: string;
  error: string | null;
}

export interface RecipePreviewInput {
  recipe: StudioRecipe;
  jurisdiction: string;
  sampleGame?: {
    key?: string;
    summary?: string;
    market?: string | null;
    gameId?: string | null;
    gameName?: string | null;
    playUrl?: string | null;
  };
}

/**
 * Tiny helper for the PDF upload, which wants the raw bytes as the body
 * rather than a JSON envelope. Keeps `api.ts` minimal; everything else uses
 * the shared `request` helper.
 */
async function postRaw<T>(path: string, body: BodyInit, contentType: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    body,
    headers: { 'Content-Type': contentType },
  });
  const json = await res.json().catch(() => ({ success: false, error: 'Invalid JSON' }));
  if (!res.ok || !json?.success) {
    const err = new Error(json?.error || `Request failed: ${res.status}`);
    (err as Error & { data?: unknown }).data = json?.data;
    throw err;
  }
  return json.data as T;
}

export const studioRecipesApi = {
  list: () => api.get<RecipeListEntry[]>('/api/studio-recipes'),
  get: (slug: string) => api.get<RecipeFetchResponse>(`/api/studio-recipes/${encodeURIComponent(slug)}`),
  save: (slug: string, recipe: StudioRecipe) =>
    fetch(`/api/studio-recipes/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(recipe),
    }).then(async (res) => {
      const json = await res.json().catch(() => ({ success: false, error: 'Invalid JSON' }));
      if (!res.ok || !json?.success) throw new Error(json?.error || `Request failed: ${res.status}`);
      return json.data as RecipeSaveResponse;
    }),
  remove: (slug: string) =>
    fetch(`/api/studio-recipes/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
    }).then(async (res) => {
      const json = await res.json().catch(() => ({ success: false, error: 'Invalid JSON' }));
      if (!res.ok || !json?.success) throw new Error(json?.error || `Request failed: ${res.status}`);
      return json.data as RecipeDeleteResponse;
    }),
  draftFromPdf: (file: File, studio?: string) => {
    const params = new URLSearchParams();
    if (studio) params.set('studio', studio);
    const path = `/api/studio-recipes/draft-from-pdf${params.toString() ? `?${params.toString()}` : ''}`;
    return postRaw<DraftSuccessResponse>(path, file, file.type || 'application/pdf');
  },
  preview: (input: RecipePreviewInput) => api.post<EnvLinkRow>('/api/studio-recipes/preview', input),
  confluenceSearch: (q: string) =>
    api.get<ConfluenceSearchHit[]>(
      `/api/studio-recipes/confluence/search?q=${encodeURIComponent(q)}`,
    ),
  confluenceSync: (slug: string, pageId: string) =>
    api.post<ConfluenceSyncResponse>(
      `/api/studio-recipes/${encodeURIComponent(slug)}/confluence/sync`,
      { pageId },
    ),
  gitlabSearch: (q: string) =>
    api.get<GitLabProjectSearchHit[]>(
      `/api/studio-recipes/gitlab/search?q=${encodeURIComponent(q)}`,
    ),
  sourceStatus: (slug: string) =>
    api.get<SourceStatusResponse>(
      `/api/studio-recipes/${encodeURIComponent(slug)}/source-status`,
    ),
};
